import { filterTeamMembersByPlan } from './membershipBookingUtils';
import { getTeamMembersWhoCanDoService, memberCanPerformBookingService } from './appointmentBookingUtils';

export const MANAGEMENT_BOOKING_CUSTOMER_EXISTING = 'existing';
export const MANAGEMENT_BOOKING_CUSTOMER_NEW = 'new';
export const MANAGEMENT_BOOKING_CUSTOMER_ANONYMOUS = 'anonymous';

const HELD_MEMBERSHIP_STATUSES = new Set(['Active', 'Trialing', 'PastDue']);

export const isHeldMembershipStatus = (status) => HELD_MEMBERSHIP_STATUSES.has(status);

export const canOfferMembershipEnrollment = (customerType) => (
  customerType === MANAGEMENT_BOOKING_CUSTOMER_EXISTING
  || customerType === MANAGEMENT_BOOKING_CUSTOMER_NEW
);

export const getHeldCustomerMemberships = (memberships, customerId) => (
  (memberships || []).filter((membership) => (
    (!customerId || membership.customerId === customerId)
    && isHeldMembershipStatus(membership.status)
  ))
);

export const getEnrollablePlans = (plans, customerMemberships) => {
  const heldPlanIds = new Set(
    getHeldCustomerMemberships(customerMemberships).map((membership) => membership.membershipPlanId),
  );

  return (plans || []).filter((plan) => (
    (!plan.status || plan.status === 'Active')
    && !heldPlanIds.has(plan.id)
  ));
};

export const getLockedServiceIds = (plan) => (
  new Set((plan?.serviceIds || []).map((serviceId) => Number(serviceId)))
);

export const mergeLockedServiceIds = (selectedServiceIds, lockedServiceIds) => {
  const next = new Set((selectedServiceIds || []).map((serviceId) => Number(serviceId)));
  lockedServiceIds.forEach((serviceId) => next.add(Number(serviceId)));
  return [...next];
};

export const toggleServiceIdWithLocks = (selectedServiceIds, serviceId, lockedServiceIds) => {
  const selected = (selectedServiceIds || []).map((id) => Number(id));
  const locked = lockedServiceIds instanceof Set
    ? lockedServiceIds
    : getLockedServiceIds({ serviceIds: lockedServiceIds });
  const numericId = Number(serviceId);

  if (locked.has(numericId)) {
    return selected.includes(numericId) ? selected : [...selected, numericId];
  }

  return selected.includes(numericId)
    ? selected.filter((id) => id !== numericId)
    : [...selected, numericId];
};

export const getProfessionalsForManagedBookingService = (teamMembers, serviceId, plan, service = null) => {
  const candidates = service?.isCombo
    ? (teamMembers || []).filter((member) => memberCanPerformBookingService(member, service))
    : getTeamMembersWhoCanDoService(teamMembers, serviceId);
  const planServiceIds = getLockedServiceIds(plan);
  if (!plan || !planServiceIds.has(Number(serviceId))) {
    return candidates;
  }

  return filterTeamMembersByPlan(candidates, plan.teamMemberIds);
};

export const PACKAGE_OPTION_PREFIX = 'package:';

// A package covers its services the same way a plan does, so the same lock and professional
// filter apply to it.
export const toPackageLockSource = (packageEntitlement) => (
  packageEntitlement
    ? {
      serviceIds: packageEntitlement.requiredServiceIds || [],
      teamMemberIds: packageEntitlement.allowedTeamMemberIds || [],
    }
    : null
);

export const buildManagementAppointmentMembershipFields = ({
  customerType,
  selectedMembershipId,
  selectedMembershipPlanId,
  selectedServicePackageId = null,
}) => {
  if (selectedServicePackageId) {
    return customerType === MANAGEMENT_BOOKING_CUSTOMER_EXISTING
      ? { customerServicePackageId: selectedServicePackageId }
      : {};
  }

  if (!canOfferMembershipEnrollment(customerType)) {
    return {};
  }

  if (selectedMembershipPlanId) {
    return { membershipPlanId: selectedMembershipPlanId };
  }

  if (selectedMembershipId) {
    return { customerMembershipId: selectedMembershipId };
  }

  return {};
};

export const resolveInitialMembershipSelection = ({
  customerMemberships,
  membershipPlans,
  customerMembershipId,
  membershipPlanId,
}) => {
  if (customerMembershipId) {
    const held = (customerMemberships || []).find((membership) => (
      String(membership.id) === String(customerMembershipId)
    ));
    if (held) {
      return { membershipId: held.id, planId: '' };
    }
  }

  if (membershipPlanId) {
    const heldForPlan = (customerMemberships || []).find((membership) => (
      String(membership.membershipPlanId) === String(membershipPlanId)
    ));
    if (heldForPlan) {
      return { membershipId: heldForPlan.id, planId: '' };
    }

    const plan = (membershipPlans || []).find((item) => String(item.id) === String(membershipPlanId));
    if (plan) {
      return { membershipId: '', planId: plan.id };
    }
  }

  return null;
};

export const buildManagementAppointmentMembershipUpdateFields = ({
  selectedMembershipId,
  selectedMembershipPlanId,
  hadMembershipCoverage,
  selectedServicePackageId = null,
  hadPackageCoverage = false,
}) => {
  const selected = buildManagementAppointmentMembershipFields({
    customerType: MANAGEMENT_BOOKING_CUSTOMER_EXISTING,
    selectedMembershipId,
    selectedMembershipPlanId,
    selectedServicePackageId,
  });
  const clearPackage = hadPackageCoverage && !selectedServicePackageId
    ? { clearServicePackageCoverage: true }
    : {};

  if (Object.keys(selected).length > 0) {
    return { ...selected, ...clearPackage };
  }

  if (hadMembershipCoverage) {
    return { clearMembershipCoverage: true, ...clearPackage };
  }

  return clearPackage;
};
